import { useRef, useState } from "react";
import { FolderLock, Loader2, Paperclip, X } from "lucide-react";
import { CoffreAttachPickerModal } from "@/features/compose/CoffreAttachPickerModal";
import {
  formatComposeAttachmentSize,
  COMPOSE_ATTACHMENT_MAX_FILES,
} from "@/lib/compose/composeAttachments";
import { prefetchCoffreList } from "@/lib/suite/coffreSuiteApi";
import type { ComposeAttachment } from "@/lib/types/compose";
import { cn } from "@/lib/cn";

type Props = {
  attachments: ComposeAttachment[];
  disabled?: boolean;
  onAddFiles: (files: FileList | File[]) => void | Promise<void>;
  onRemove: (id: string) => void;
  error?: string | null;
  /** Backdrop Coffre : transparent = tiroir compose (évite double blur). */
  suitePickerBackdrop?: "default" | "transparent" | "stacked";
};

const attachBtnClass =
  "compose-attach-source-btn inline-flex h-7 w-7 items-center justify-center rounded-full border border-[var(--mail-border-soft)] text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)] disabled:opacity-40";

export function ComposeAttachments({
  attachments,
  disabled = false,
  onAddFiles,
  onRemove,
  error,
  suitePickerBackdrop = "stacked",
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [coffreOpen, setCoffreOpen] = useState(false);
  const atLimit = attachments.length >= COMPOSE_ATTACHMENT_MAX_FILES;
  const blocked = disabled || atLimit;

  return (
    <>
      <div className="compose-attachments-bar shrink-0 border-t border-[var(--mail-border-soft)] px-3 py-1.5 sm:px-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="compose-attach-source-group flex shrink-0 items-center gap-1">
            <button
              type="button"
              disabled={blocked}
              onClick={() => inputRef.current?.click()}
              className={attachBtnClass}
              aria-label="Joindre un fichier local"
              title="Joindre un fichier (ou glisser-déposer)"
            >
              <Paperclip className="h-3.5 w-3.5" aria-hidden />
            </button>
            <button
              type="button"
              disabled={blocked}
              onClick={() => setCoffreOpen(true)}
              onPointerEnter={() => prefetchCoffreList("", "vault")}
              className={attachBtnClass}
              aria-label="Joindre depuis Coffre"
            >
              <FolderLock className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>

          <input
            ref={inputRef}
            type="file"
            multiple
            className="sr-only"
            disabled={disabled}
            onChange={(e) => {
              const files = e.target.files;
              if (files?.length) void onAddFiles(files);
              e.target.value = "";
            }}
          />

          {attachments.map((file) => (
            <span
              key={file.id}
              className={cn(
                "inline-flex max-w-[min(100%,14rem)] items-center gap-1 rounded-full py-1 pl-2.5 pr-1 text-[11px] text-[var(--mail-text)]",
                file.loading
                  ? "bg-[var(--mail-accent-soft)]/60"
                  : "bg-[var(--mail-surface-hover)]"
              )}
            >
              {file.loading ? (
                <Loader2
                  className="h-3 w-3 shrink-0 animate-spin text-[var(--mail-accent)]"
                  aria-hidden
                />
              ) : null}
              <span className="truncate" title={file.filename}>
                {file.filename}
              </span>
              {!file.loading ? (
                <span className="shrink-0 text-[var(--mail-muted)]">
                  ({formatComposeAttachmentSize(file.size)})
                </span>
              ) : (
                <span className="shrink-0 text-[var(--mail-muted)]">(chargement…)</span>
              )}
              <button
                type="button"
                disabled={disabled || file.loading}
                onClick={() => onRemove(file.id)}
                className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[var(--mail-muted)] hover:bg-[var(--mail-surface)] hover:text-[var(--mail-text)] disabled:opacity-40"
                aria-label={`Retirer ${file.filename}`}
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          ))}
        </div>
        {error ? (
          <p className="mt-1.5 text-[11px] text-[var(--mail-danger-text)]" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <CoffreAttachPickerModal
        open={coffreOpen}
        onClose={() => setCoffreOpen(false)}
        onConfirm={onAddFiles}
        overlayBackdrop={suitePickerBackdrop}
      />
    </>
  );
}
